import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FaStore, FaTruck, FaUserFriends, FaBoxOpen, FaShoppingCart } from "react-icons/fa";


const Counter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const increment = end / (duration / 20);

    const interval = setInterval(() => {
      start += increment;
      if (start >= end) {
        clearInterval(interval);
        setCount(end);
      } else {
        setCount(Math.ceil(start));
      }
    }, 20);

    return () => clearInterval(interval);
  }, [end, duration]);

  return (
    <span>
      {count}
      {suffix}
    </span>
  );
};

const BannerCarousel = () => {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [textVisible, setTextVisible] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const toDataUrl = useCallback((img, mime = "jpeg") => {
    if (!img) return "";
    if (img.startsWith("data:image")) return img;
    if (/^https?:\/\//i.test(img)) return img;
    return `data:image/${mime};base64,${img}`;
  }, []);

  const styles = {
    counterRow: {
      position: "absolute",
      bottom: "23px",
      left: "56%",
      transform: "translateX(-50%)",
      display: "grid",
      gridTemplateColumns: "repeat(5, 1fr)", // fixed 5 counters ek line me
      gap: "15px",
      padding: "10px 15px",
      borderRadius: "15px",
      zIndex: 5,
      width: "100%",
      maxWidth: "1000px",
      textAlign: "center",
    },
    counterBox: { color: "#fff" },
    counterNumber: { fontSize: "40px", fontWeight: "bold", margin: "0" },
    counterText: { fontSize: "20px", margin: "5px 0 0" },
    icon: {
      fontSize: "35px",
      marginBottom: "10px",
      color: "white",
    },
  };

  const responsiveStyle = `
  @media (max-width: 768px) {
    .counter-number { font-size: 24px !important; }
    .counter-text { font-size: 14px !important; }
    .counter-icon { font-size: 24px !important; }
    .counter-row { gap: 10px !important; }
  }

  @media (max-width: 480px) {
    .counter-row {
      grid-template-columns: repeat(5, 1fr) !important; /* 5 ek row me hi */
    }
    .counter-number { font-size: 15px !important; }
    .counter-text { font-size: 9px !important; }
    .counter-icon { font-size: 16px !important; }
    .counter-row { gap: 5px !important; padding: 4px !important; }
  }
`;

  /* ---------------------------
     Resize listener
  ---------------------------- */
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ---------------------------
     Fetch banner data
  ---------------------------- */
  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const token = localStorage.getItem("authToken"); // ✅ Get stored token

        const response = await axios.get(
          "https://backend.maamaas.com/food/api/bannner/getall",
          {
            headers: {
              Authorization: `Bearer ${token}`, // ✅ Send token with request
            },
          }
        );

        if (Array.isArray(response.data) && response.data.length > 0) {
          const processed = response.data.map((b) => ({
            ...b,
            companyLogo: toDataUrl(b.companyLogo),
            companyBanner: toDataUrl(b.companyBanner),
          }));
          setBanners(processed);
        }
      } catch (error) {
        console.error("❌ Failed to fetch banners:", error);
      }
    };

    fetchBanner();
  }, [toDataUrl]);



  const [counts, setCounts] = useState({
    vendors: 0,
    users: 0,
    products: 0,
    happyCustomers: 0,
  });

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(
          "https://backend.maamaas.com/food/api/orders"
        );

        if (Array.isArray(response.data)) {
          const uniqueUserIds = [...new Set(response.data.map(o => o.userId))];
          const uniqueVendorIds = [...new Set(response.data.map(o => o.vendorId))];

          setCounts({
            vendors: uniqueVendorIds.length,
            users: uniqueUserIds.length,
            products: 120, // Replace with actual API when available
            happyCustomers: response.data.length, // total orders as happy customers
          });
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    };

    fetchOrders();
  }, []);

  /* ---------------------------
     Text animation
  ---------------------------- */
  useEffect(() => {
    const timer = setTimeout(() => setTextVisible(true), 500);
    return () => clearTimeout(timer);
  }, [currentIndex]);

  /* ---------------------------
     Auto-slide
  ---------------------------- */
  useEffect(() => {
    if (!banners.length) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % banners.length);
      setTextVisible(false);
    }, 5000);
    return () => clearInterval(interval);
  }, [banners]);

  const currentBanner = banners[currentIndex];

  return (
    <div style={{ width: "100%", margin: 0, padding: 0, fontFamily: "'Poppins', sans-serif" }}>
      <div style={{ position: "relative", overflow: "hidden", boxShadow: "0 4px 12px rgba(0,0,0,0.2)" }}>
        {/* Overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.3))",
            zIndex: 2,
          }}
        ></div>

        {/* Slider */}
        <div
          style={{
            display: "flex",
            transition: "transform 0.8s ease-in-out",
            transform: `translateX(-${currentIndex * 100}%)`,
          }}
        >
          {banners.map((b, index) => (
            <img
              key={b.bannerId || index}
              src={b.companyBanner}
              alt={`banner-${index}`}
              style={{
                flexShrink: 0,
                width: "100%",
                height: windowWidth <= 768 ? "400px" : "700px",
                objectFit: "cover",
              }}
            />
          ))}
        </div>

        {/* Logo */}
        {currentBanner?.companyLogo && (
          <img
            src={currentBanner.companyLogo}
            alt="logo"
            style={{
              position: "absolute",
              top: windowWidth <= 768 ? "75px" : "220px",
              left: windowWidth <= 768 ? "10px" : "40px",
              height: windowWidth <= 768 ? "60px" : "100px",
              width: "auto",
              zIndex: 4,
              objectFit: "contain",
            }}
          />
        )}

        {/* Text Overlay */}
        <div
          style={{
            position: "absolute",
            top: windowWidth <= 768 ? "36%" : "50%",
            left: windowWidth <= 768 ? "15px" : "40px",
            right: windowWidth <= 768 ? "15px" : "40px",
            zIndex: 5,
            display: "flex",
            flexDirection: windowWidth <= 768 ? "column" : "row",
            gap: "20px",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: "280px",
              color: "#fff",
              textAlign: "left",
              maxWidth: "600px",
              transform: textVisible ? "translateY(0)" : "translateY(-15%)",
              opacity: textVisible ? 1 : 0,
              transition: "all 0.8s ease-in-out",
            }}
          >
            <h1
              style={{
                fontSize: windowWidth <= 768 ? "1.8rem" : "3rem",
                fontWeight: "bold",
                marginTop: windowWidth <= 768 ? "0" : "-20px",
                letterSpacing: "1px",
                textShadow: "2px 2px 6px rgba(0,0,0,0.8)",
                lineHeight: "1.2",
              }}
            >
              {currentBanner?.companyName || "Maamaas House"}
            </h1>
            <p
              style={{
                fontSize: windowWidth <= 768 ? "1rem" : "1.5rem",
                marginTop: "5px",
                fontWeight: 300,
                textShadow: "1px 1px 4px rgba(0,0,0,0.6)",
              }}
            >
              Since {currentBanner?.establishedYear || "1997"}
            </p>
          </div>
        </div>



        <style>{responsiveStyle}</style>
        {/* <div style={styles.counterRow} className="counter-row">

          <div style={styles.counterBox}>
            <FaStore className="counter-icon" style={styles.icon} />
            <h2 style={styles.counterNumber} className="counter-number">
              {counts.vendors}+
            </h2>
            <p style={styles.counterText} className="counter-text">Partners</p>
          </div>



          <div style={styles.counterBox}>
            <FaUserFriends className="counter-icon" style={styles.icon} />
            <h2 style={styles.counterNumber} className="counter-number">
              {counts.users}+
            </h2>
            <p style={styles.counterText} className="counter-text">Users</p>
          </div>

          <div style={styles.counterBox}>
            <FaBoxOpen className="counter-icon" style={styles.icon} />
            <h2 style={styles.counterNumber} className="counter-number">
              {counts.products}+
            </h2>
            <p style={styles.counterText} className="counter-text">Products</p>
          </div>

          <div style={styles.counterBox}>
            <FaShoppingCart className="counter-icon" style={styles.icon} />
            <h2 style={styles.counterNumber} className="counter-number">
              {counts.happyCustomers}+
            </h2>
            <p style={styles.counterText} className="counter-text">Total Orders</p>
          </div>



      </div> */}
      </div>
    </div >
  );
};

export default BannerCarousel;
